import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { compare } from 'fast-json-patch'

import { useCreateClub } from '../../../../../hooks/clubs/useCreateClub'
import { useUpdateClub } from '../../../../../hooks/clubs/useUpdateClub'

import { Card } from '../../../../card/Card'
import Address from '../../../../address/Address'
import { ClubFormSection } from './clubFormSection/ClubFormSection'

import { useApp } from '../../../../../contexts/AppContext'
import { ClubModel } from '../../../../../types/court/court'
import { BannerKind } from '../../../../../types/contexts/AppContext'
import { browserTimeZone } from '../../../../../utils/timeZones'

type ClubEditProps = {
  club: ClubModel | null
  isEditMode: boolean
  onActionsChange: (actions: ClubEditActions) => void
}

export type ClubEditActions = {
  save: () => void
  reset: () => void
  isDirty: boolean
  isValid: boolean
  loading: boolean
}

const toClubModel = (club?: Partial<ClubModel> | null): ClubModel => ({
  id: club?.id,
  name: club?.name ?? '',
  openTime: club?.openTime ?? '08:00',
  closeTime: club?.closeTime ?? '22:00',
  freeCancellation: club?.freeCancellation ?? 12,
  startIncrementMinutes: club?.startIncrementMinutes ?? 30,
  sessionDurations: [...(club?.sessionDurations ?? [60, 90])].sort((a, b) => a - b),
  maxReschedules: club?.maxReschedules ?? 3,
  passExtensionDays: club?.passExtensionDays ?? 90,
  timeZone: club?.timeZone ?? browserTimeZone(),
  address: {
    id: club?.address?.id,
    county: club?.address?.county ?? '',
    city: club?.address?.city ?? '',
    street: club?.address?.street ?? '',
    number: club?.address?.number ?? '',
    floor: club?.address?.floor ?? '',
    apartment: club?.address?.apartment ?? '',
    postalCode: club?.address?.postalCode ?? '',
  },
})

export const ClubEdit = ({
  club,
  isEditMode,
  onActionsChange,
}: ClubEditProps) => {
  const navigate = useNavigate()
  const { showBanner } = useApp()
  const { t } = useTranslation('clubs')
  const [initialDetails, setInitialDetails] = useState<ClubModel | null>(null)
  const [details, setDetails] = useState<ClubModel>(() => toClubModel(club))
  const [detailsAreValid, setDetailsAreValid] = useState(false)
  const [addressIsValid, setAddressIsValid] = useState(false)
  const [formVersion, setFormVersion] = useState(0)

  const {
    createClub,
    loading: createLoading,
    error: createError,
    success: createSuccess,
    resetState: resetCreateState,
  } = useCreateClub()

  const {
    updateClub,
    loading: updateLoading,
    error: updateError,
    success: updateSuccess,
    resetState: resetUpdateState,
  } = useUpdateClub()

  useEffect(() => {
    const model = toClubModel(club)
    setInitialDetails(isEditMode ? model : null)
    setDetails(model)
    setFormVersion(previous => previous + 1)
  }, [club, isEditMode])

  useEffect(() => {
    const error = createError || updateError
    const success = createSuccess || updateSuccess

    if (error) {
      showBanner({
        kind: BannerKind.ERR,
        msg: error,
      })
    }

    if (success) {
      showBanner({
        kind: BannerKind.OK,
        msg: success,
      })
    }

    resetState()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createError, createSuccess, updateError, updateSuccess])

  const resetState = () => {
    resetCreateState()
    resetUpdateState()
  }

  const initialModel = useMemo(
    () => initialDetails ?? toClubModel(),
    [initialDetails]
  )

  const patch = useMemo(
    () => compare(initialModel, details),
    [initialModel, details]
  )

  const isDirty = patch.length > 0
  const loading = createLoading || updateLoading
  const isValid = detailsAreValid && addressIsValid
  const synchronizeDetails = (savedDetails: ClubModel) => {
    const savedModel = toClubModel(savedDetails)
    setInitialDetails(savedModel)
    setDetails(savedModel)
    setFormVersion(previous => previous + 1)
  }

  const handleSave = async () => {
    if (!isValid || loading) {
      return
    }

    if (!isEditMode) {
      const created = await createClub(details)

      if (created) {
        navigate('/m/clubs', { replace: true })
      }

      return
    }

    if (details.id === undefined || patch.length === 0) {
      return
    }

    const updated = await updateClub(details.id, patch)

    if (!updated) {
      return
    }

    synchronizeDetails(updated)
    navigate('/m/clubs', { replace: true })
  }

  const handleCancel = () => {
    setDetails(initialDetails ? toClubModel(initialDetails) : toClubModel())
    setFormVersion(previous => previous + 1)
  }

  useEffect(() => {
    onActionsChange({
      save: () => void handleSave(),
      reset: handleCancel,
      isDirty,
      isValid,
      loading,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details, initialDetails, isDirty, isValid, loading, onActionsChange])

  return (
    <div className="row g-3">
      <Card className="col-12 club-edit-card">
        <ClubFormSection
          key={`club-details-${formVersion}`}
          details={details}
          isEditMode={isEditMode}
          onChange={setDetails}
          onValidityChange={setDetailsAreValid}
        />
      </Card>
      <Card className="col-12 club-edit-card">
        <Address
          key={`club-address-${formVersion}`}
          address={details.address}
          title={t('clubs.address.title')}
          subtitle={t('clubs.address.subtitle')}
          requiredOnly
          loading={loading}
          showActions={false}
          onValidityChange={setAddressIsValid}
          onChange={address =>
            setDetails(previous => ({
              ...previous,
              address,
            }))
          }
        />
      </Card>
    </div>
  )
}
